import type { User as OidcUser } from "oidc-client-ts";
import { defineStore } from "pinia";

import { api, installAuthInterceptors } from "@/services/api";
import {
  beginLogin,
  beginLogout,
  clearStoredUser,
  completeLogin,
  getStoredUser,
  renewUser,
} from "@/services/keycloakAuth";
import type { Role, User } from "@/types";

const STORAGE_KEY_USER = "coordinator.auth.user";
const STORAGE_KEY_EXPIRES = "coordinator.auth.expiresAt";
/** Legacy Google / app-JWT session keys — cleared on hydrate. */
const LEGACY_ACCESS_KEY = "coordinator:secure:auth.access";
const LEGACY_REFRESH_KEY = "coordinator:secure:auth.refresh";

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  idToken: string | null;
  expiresAt: number | null;
  hydrated: boolean;
}

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    user: readStoredUser(),
    accessToken: null,
    refreshToken: null,
    idToken: null,
    expiresAt: readStoredExpiresAt(),
    hydrated: false,
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.accessToken),
    roles: (state): Role[] => state.user?.roles ?? [],
    isCoordinator(): boolean {
      return this.roles.includes("coordinator") || this.roles.includes("admin");
    },
    isAdmin(): boolean {
      return this.roles.includes("admin");
    },
  },
  actions: {
    installInterceptors(): void {
      installAuthInterceptors({
        getAccessToken: () => this.accessToken,
        getRefreshToken: () => this.refreshToken,
        refresh: () => this.refreshAccessToken(),
        onLogout: () => this.signOut(),
      });
    },

    _setOidcTokens(oidcUser: OidcUser): void {
      this.accessToken = oidcUser.access_token;
      this.refreshToken = oidcUser.refresh_token || null;
      this.idToken = oidcUser.id_token || null;
      this.expiresAt = oidcUser.expires_at
        ? oidcUser.expires_at * 1000
        : Date.now() + (oidcUser.expires_in || 0) * 1000;
      localStorage.setItem(STORAGE_KEY_EXPIRES, String(this.expiresAt));
    },

    _setProfile(profile: User): void {
      this.user = profile;
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(profile));
    },

    _clearSession(): void {
      this.accessToken = null;
      this.refreshToken = null;
      this.idToken = null;
      this.expiresAt = null;
      this.user = null;
      localStorage.removeItem(STORAGE_KEY_USER);
      localStorage.removeItem(STORAGE_KEY_EXPIRES);
      localStorage.removeItem(LEGACY_ACCESS_KEY);
      localStorage.removeItem(LEGACY_REFRESH_KEY);
    },

    _clearLegacyStorage(): void {
      localStorage.removeItem(LEGACY_ACCESS_KEY);
      localStorage.removeItem(LEGACY_REFRESH_KEY);
    },

    /**
     * Load profile + roles from the nbcallback API.
     * Keycloak is identity; /auth/me is the shared source of app roles
     * (same technician / coordinator / admin model as bridge-mapview).
     */
    async _fetchProfile(): Promise<User> {
      const { data } = await api.get<User>("/auth/me");
      this._setProfile(data);
      return data;
    },

    /** Restore Keycloak session on app mount / route guard. */
    async hydrate(): Promise<void> {
      if (this.hydrated && this.accessToken && this.user) return;

      this._clearLegacyStorage();

      const stored = await getStoredUser();
      if (!stored) {
        this.hydrated = true;
        return;
      }

      if (stored.expired) {
        const renewed = await this.refreshAccessToken();
        if (!renewed) {
          this.hydrated = true;
          return;
        }
      } else {
        this._setOidcTokens(stored);
      }

      if (!this.user) {
        try {
          await this._fetchProfile();
        } catch {
          this._clearSession();
          await clearStoredUser();
        }
      }

      this.hydrated = true;
    },

    /** Redirect the browser to Keycloak hosted login (does not return). */
    async signInWithSso(): Promise<void> {
      await beginLogin();
    },

    /** Complete OIDC redirect on /auth/callback: exchange code, load profile. */
    async completeSsoLogin(): Promise<void> {
      const oidcUser = await completeLogin();
      this._setOidcTokens(oidcUser);
      await this._fetchProfile();
      this.hydrated = true;
    },

    /**
     * Silently refresh the access token via Keycloak refresh_token grant.
     * @returns new access token, or null on failure
     */
    async refreshAccessToken(): Promise<string | null> {
      try {
        const oidcUser = await renewUser();
        this._setOidcTokens(oidcUser);
        try {
          await this._fetchProfile();
        } catch {
          // Keep tokens even if /auth/me fails transiently.
        }
        return oidcUser.access_token;
      } catch {
        this._clearSession();
        await clearStoredUser();
        return null;
      }
    },

    /** Clear local session and redirect to Keycloak end-session (SSO logout). */
    async signOut(): Promise<void> {
      const idToken = this.idToken;
      this._clearSession();
      await clearStoredUser();
      await beginLogout(idToken || undefined);
    },
  },
});

function readStoredUser(): User | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_USER);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function readStoredExpiresAt(): number | null {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(STORAGE_KEY_EXPIRES);
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}
